/**
 * Stepper — vertical progress rail for the onboarding flow.
 * Presentational: the caller owns `current` and handles `onStepClick`.
 * Sticky positioning is applied by the caller (e.g. `sticky top-[92px]`).
 */
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface StepperStep {
  label: string;
  sub?: string;
}

export interface StepperProps {
  steps: StepperStep[];
  /** Zero-based index of the active step. */
  current: number;
  /** Called with the clicked step's index. */
  onStepClick?: (index: number) => void;
  className?: string;
}

export function Stepper({ steps, current, onStepClick, className }: StepperProps) {
  return (
    <ol className={className}>
      {steps.map((step, i) => {
        const status = i < current ? 'done' : i === current ? 'current' : 'todo';
        const isLast = i === steps.length - 1;
        return (
          <li key={step.label}>
            <button
              type="button"
              onClick={() => onStepClick?.(i)}
              disabled={status === 'todo'}
              aria-current={status === 'current' ? 'step' : undefined}
              aria-disabled={status === 'todo' ? true : undefined}
              className="flex w-full gap-3 text-left"
            >
              <div className="flex flex-col items-center">
                <span
                  className={cn(
                    'flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                    status === 'done' && 'bg-primary text-primary-foreground',
                    status === 'current' && 'border-2 border-primary text-primary',
                    status === 'todo' && 'border border-input text-faint',
                  )}
                >
                  {status === 'done' ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </span>
                {!isLast && (
                  <span
                    className={cn(
                      'min-h-[22px] w-0.5 flex-1',
                      i < current ? 'bg-primary' : 'bg-border-subtle',
                    )}
                  />
                )}
              </div>
              <div className={cn(!isLast && 'pb-8')}>
                <div
                  className={cn(
                    'text-sm',
                    status === 'todo' ? 'font-medium text-faint' : 'font-semibold text-foreground',
                  )}
                >
                  {step.label}
                </div>
                {step.sub && status !== 'todo' && (
                  <div className="mt-0.5 text-[12.5px] text-faint">{step.sub}</div>
                )}
              </div>
            </button>
          </li>
        );
      })}
    </ol>
  );
}
